import { useEffect, useState } from "react";
import { HandHeart } from "lucide-react";

const HADITHS = [
  {
    text: "Whoever builds a masjid for Allah, Allah will build for him a similar house in Jannah.",
    src: "Sahih Muslim",
  },
  {
    text: "The most beloved of deeds to Allah are those that are most consistent, even if they are small.",
    src: "Sahih al-Bukhari",
  },
  {
    text: "None of you truly believes until he loves for his brother what he loves for himself.",
    src: "Sahih al-Bukhari & Muslim",
  },
  {
    text: "Smiling at your brother is charity.",
    src: "Jami` at-Tirmidhi",
  },
];

/**
 * Eye-catching Bismillah calligraphy card + rotating hadith strip.
 * Replaces the old image-driven welcome tiles. Pure CSS/SVG — no remote
 * images, so this can never break from storage outages.
 */
export default function BismillahShowcase() {
  const [hi, setHi] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setHi((i) => (i + 1) % HADITHS.length), 8000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="lg:col-span-7 grid grid-cols-6 gap-4">
      {/* MAIN BISMILLAH CARD */}
      <div
        className="bismillah-card col-span-6 sm:col-span-4 aspect-[4/5] rounded-3xl relative overflow-hidden flex items-center justify-center"
        data-testid="bismillah-card"
      >
        {/* Layered backgrounds */}
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy-800 to-navy-900" />
        <div className="absolute inset-0 pattern-girih opacity-25" />
        {/* Slowly rotating 8-point star ornament */}
        <svg
          className="absolute -top-8 -right-12 w-72 h-72 opacity-[0.10] bismillah-rotate"
          viewBox="0 0 200 200" fill="none"
          stroke="#C5A059" strokeWidth="0.8"
        >
          <path d="M100 10 L130 70 L190 100 L130 130 L100 190 L70 130 L10 100 L70 70 Z" />
          <path d="M100 30 L122 78 L170 100 L122 122 L100 170 L78 122 L30 100 L78 78 Z" />
          <path d="M100 55 L114 86 L145 100 L114 114 L100 145 L86 114 L55 100 L86 86 Z" />
        </svg>
        <svg
          className="absolute -bottom-12 -left-10 w-56 h-56 opacity-[0.08] bismillah-rotate-rev"
          viewBox="0 0 200 200" fill="none"
          stroke="#C5A059" strokeWidth="0.7"
        >
          <path d="M100 10 L130 70 L190 100 L130 130 L100 190 L70 130 L10 100 L70 70 Z" />
          <path d="M100 30 L122 78 L170 100 L122 122 L100 170 L78 122 L30 100 L78 78 Z" />
        </svg>

        {/* Radial gold glow */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(197,160,89,0.22),transparent_60%)]" />

        {/* Floating gold sparkles */}
        <span className="bismillah-sparkle absolute top-[18%] left-[15%] w-1 h-1 rounded-full bg-gold/70" />
        <span className="bismillah-sparkle-2 absolute top-[28%] right-[18%] w-1.5 h-1.5 rounded-full bg-gold/55" />
        <span className="bismillah-sparkle-3 absolute bottom-[24%] left-[24%] w-1 h-1 rounded-full bg-gold/65" />
        <span className="bismillah-sparkle absolute bottom-[18%] right-[12%] w-2 h-2 rounded-full bg-gold/40" />
        <span className="bismillah-sparkle-2 absolute top-[55%] left-[8%] w-0.5 h-0.5 rounded-full bg-gold/80" />

        {/* CONTENT */}
        <div className="relative z-10 flex flex-col items-center text-center px-6">
          <div className="text-[10px] uppercase tracking-[0.4em] text-gold font-semibold mb-6">
            <span className="inline-block w-6 h-px bg-gold/60 align-middle mr-3" />
            In the name of Allah
            <span className="inline-block w-6 h-px bg-gold/60 align-middle ml-3" />
          </div>

          {/* Bismillah Arabic with shimmer overlay */}
          <div className="relative">
            <h2
              dir="rtl"
              lang="ar"
              className="font-arabic text-cream leading-[1.3] text-[2.2rem] sm:text-[2.6rem] md:text-[2.9rem] lg:text-[3.1rem] xl:text-[3.4rem] tracking-wide select-text"
              style={{ textShadow: "0 2px 24px rgba(197,160,89,0.35)" }}
            >
              بِسْمِ ٱللَّٰهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
            </h2>
            {/* Gold sweep overlay */}
            <div className="bismillah-sweep absolute inset-0 pointer-events-none" />
          </div>

          {/* Hand-drawn gold underline */}
          <svg className="mt-6 w-44 h-3" viewBox="0 0 200 12" fill="none" preserveAspectRatio="none">
            <path
              d="M2 6 Q 50 1, 100 6 T 198 6"
              stroke="#C5A059" strokeWidth="1.1" strokeLinecap="round"
              className="bismillah-underline"
            />
            <circle cx="100" cy="6" r="2.2" fill="#C5A059" />
          </svg>

          <p className="mt-6 max-w-xs text-cream/75 text-[0.92rem] leading-relaxed font-serif italic">
            "In the name of Allah, the Most Gracious, the Most Merciful."
          </p>
        </div>

        {/* Subtle inner border */}
        <div className="absolute inset-3 rounded-[1.4rem] border border-gold/15 pointer-events-none" />
      </div>

      {/* RIGHT COLUMN — feature tile + rotating hadith */}
      <div className="col-span-6 sm:col-span-2 flex flex-col gap-4">
        {/* Small ornamental tile — 99 names tease */}
        <div className="aspect-square rounded-3xl relative overflow-hidden bg-gradient-to-br from-gold/15 via-cream to-sand">
          <div className="absolute inset-0 pattern-girih opacity-30" />
          <div className="relative h-full p-5 flex flex-col items-center justify-center text-center">
            <div
              dir="rtl"
              lang="ar"
              className="font-arabic text-navy text-4xl md:text-5xl leading-none"
              style={{ textShadow: "0 1px 0 rgba(197,160,89,0.25)" }}
            >
              ٩٩
            </div>
            <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-navy/70 font-semibold">
              Names of Allah
            </div>
            <div className="mt-1 text-[11px] text-navy/55 font-serif italic">Asma ul-Husna</div>
          </div>
        </div>

        {/* HADITH tile (rotating) */}
        <div className="flex-1 rounded-3xl bg-navy text-cream p-6 flex flex-col justify-end relative overflow-hidden">
          <div className="absolute inset-0 pattern-girih opacity-25" />
          <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-gold/[0.07] blur-2xl" />
          <div className="relative">
            <HandHeart size={26} className="text-gold mb-3" />
            <div className="relative min-h-[8rem]" data-testid="hadith-rotator">
              {HADITHS.map((h, i) => (
                <div
                  key={i}
                  aria-hidden={i !== hi}
                  className={`transition-opacity duration-700 ${
                    i === hi ? "opacity-100 relative" : "opacity-0 absolute inset-0 pointer-events-none"
                  }`}
                >
                  <p className="text-[0.92rem] leading-relaxed italic text-cream/90">
                    "{h.text}"
                  </p>
                  <div className="text-[11px] text-cream/55 mt-3">— {h.src}</div>
                </div>
              ))}
            </div>
            {/* indicator dots */}
            <div className="flex gap-1.5 mt-4">
              {HADITHS.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Show hadith ${i + 1}`}
                  onClick={() => setHi(i)}
                  className={`h-1 rounded-full transition-all ${
                    i === hi ? "w-6 bg-gold" : "w-1.5 bg-cream/25 hover:bg-cream/45"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
